import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import { siteInfo } from '@/mocks/movies';

export default function About() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        foundingDate: siteInfo.founded,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressCountry: 'CN',
          streetAddress: siteInfo.address,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: siteInfo.phone,
          email: siteInfo.email,
          contactType: 'customer service',
        },
      },
      {
        '@type': 'AboutPage',
        name: `关于我们 - ${siteInfo.name}`,
        description: `${siteInfo.name}品牌介绍、用户口碑和联系方式`,
        url: 'https://www.97cinema.com/about',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.97cinema.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  const milestones = [
    { year: '2018', title: '平台创立', description: '97影院正式上线，以免费高清电影为初心，开启观影新体验' },
    { year: '2019', title: '内容扩充', description: '电影资源突破5000部，覆盖八大电影类型，月活用户突破100万' },
    { year: '2020', title: '技术升级', description: '全面升级蓝光画质，引入智能CDN加速，实现流畅不卡顿播放' },
    { year: '2021', title: '用户体验', description: '优化界面设计，新增个性化推荐，让每位用户都能找到喜欢的电影' },
    { year: '2023', title: '飞速发展', description: '电影资源突破10000部，日访问用户超过50万，成为领先的免费观影平台' },
    { year: '2026', title: 'AI赋能', description: '引入AI智能推荐系统，推出8K超高清画质，持续引领在线观影新潮流' },
  ];

  const testimonials = [
    {
      name: '张先生',
      role: '资深影迷',
      content: '97影院的画质真的没话说！蓝光高清加上流畅播放，观影体验比很多付费平台都好。分类也很全面，想看的电影基本都能找到。',
      rating: 5,
    },
    {
      name: '李女士',
      role: '家庭用户',
      content: '周末和家里人一起在97影院看电影已经成为我们家的固定节目了。动画片资源很丰富，孩子特别喜欢，而且完全免费，太良心了！',
      rating: 5,
    },
    {
      name: '王同学',
      role: '大学生',
      content: '作为学生党，97影院简直就是宝藏网站！最新上映的电影更新很快，不用花钱去电影院，在宿舍就能享受高清观影，强烈推荐。',
      rating: 5,
    },
    {
      name: '刘先生',
      role: '上班族',
      content: '下班回家打开97影院看部好电影，一天的疲惫都消散了。界面清爽没有乱七八糟的广告，而且真的不卡顿，这点必须点赞。',
      rating: 5,
    },
    {
      name: '陈女士',
      role: '电影爱好者',
      content: '从2019年就开始用97影院了，见证了平台的成长。电影资源越来越丰富，画质也越来越好。客服响应也很快，有什么问题都能及时解决。',
      rating: 5,
    },
    {
      name: '赵先生',
      role: '自媒体博主',
      content: '做电影解说视频经常需要找素材，97影院是我最常用的工具。资源全、画质好、不卡顿，这三个优势让我工作效率大大提升。',
      rating: 5,
    },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 97影院 | 品牌介绍、用户口碑与联系方式"
        description="97影院成立于2018年，致力于为广大影迷提供最新电影、热播电影、经典电影免费在线观看服务。了解97影院的品牌故事、用户口碑和联系方式。"
        keywords="97影院,关于我们,品牌介绍,用户口碑,联系方式,免费电影平台"
        canonical="https://www.97cinema.com/about"
        ogTitle="关于我们 - 97影院"
        ogDescription="了解97影院的品牌故事、用户口碑和联系方式"
      />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              <section className="mt-8 text-center">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50">
                  关于<span className="text-primary-500">97影院</span>
                </h1>
                <p className="mt-4 text-foreground-300 max-w-2xl mx-auto leading-relaxed">
                  {siteInfo.description}
                </p>
                <p className="mt-4 text-foreground-400 max-w-2xl mx-auto leading-relaxed">
                  自{siteInfo.founded}年成立以来，97影院始终坚持"免费、高清、流畅"的核心理念，致力于为广大影迷提供最优质的在线观影体验。我们不断扩充电影资源库，优化播放技术，让每一位用户都能在这里享受纯粹的观影乐趣。
                </p>
              </section>

              <section className="mt-12">
                <h2 className="text-2xl font-heading font-bold text-foreground-50 text-center">
                  发展<span className="text-primary-500">历程</span>
                </h2>
                <div className="mt-8 relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
                  <div className="space-y-8">
                    {milestones.map((item, index) => (
                      <div key={item.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                        <div className="hidden md:block flex-1"></div>
                        <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-foreground-50"></div>
                        </div>
                        <div className="flex-1 bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
                          <span className="text-sm font-heading font-bold text-primary-500">{item.year}</span>
                          <h3 className="mt-1 text-base font-semibold text-foreground-100">{item.title}</h3>
                          <p className="mt-1 text-sm text-foreground-400">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mt-14">
                <h2 className="text-2xl font-heading font-bold text-foreground-50 text-center">
                  用户<span className="text-primary-500">口碑</span>
                </h2>
                <p className="mt-2 text-sm text-foreground-400 text-center">来自真实用户的评价，是我们前进的动力</p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {testimonials.map((t, i) => (
                    <div key={i} className="bg-background-100 border border-background-200 rounded-lg p-5">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="mt-3 text-sm text-foreground-300 leading-relaxed">{t.content}</p>
                      <div className="mt-4 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-500 text-sm font-semibold">
                          {t.name[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground-100">{t.name}</div>
                          <div className="text-xs text-foreground-500">{t.role}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-14 bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
                <h2 className="text-2xl font-heading font-bold text-foreground-50 text-center">
                  联系<span className="text-primary-500">我们</span>
                </h2>
                <p className="mt-2 text-sm text-foreground-400 text-center">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-100">电子邮箱</h3>
                    <p className="mt-1 text-sm text-foreground-400">{siteInfo.email}</p>
                  </div>
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-100">客服电话</h3>
                    <p className="mt-1 text-sm text-foreground-400">{siteInfo.phone}</p>
                  </div>
                  <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-100">公司地址</h3>
                    <p className="mt-1 text-sm text-foreground-400">{siteInfo.address}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}